import { useState } from 'react';
import { useSelector } from 'react-redux';
import { captureEvent } from '../../../../services/event-capture';
import { useAppDispatch } from '../../../../services/store';
import {
  followMember,
  getAllFollowsForCurrentMemberWithSignal,
  unfollow,
} from '../../../../services/store/modules/follows';

type FollowData = {
  id: string;
  followee: { displayName: string; id: string };
};

const useSignalFollows = () => {
  const dispatch = useAppDispatch();
  const [removeFollowError, setError] = useState(false);
  const [removingFollow, setRemovingFollow] = useState(false);

  const [followToBeRemoved, setFollowToBeRemoved] = useState<FollowData | null>(
    null,
  );

  const follows = useSelector(getAllFollowsForCurrentMemberWithSignal);

  const handleFollowMember = async (memberId: string) => {
    captureEvent({
      type: 'signal: follow user',
      payload: {
        memberId,
      },
      sendToAmplitude: true,
    });
    try {
      await dispatch(followMember(memberId)).unwrap();
    } catch (e) {
      setError(true);
    }
  };

  const handleUnfollow = (selectedFollow: FollowData) => {
    captureEvent({
      type: 'signal: select to unfollow',
      payload: {},
      sendToAmplitude: true,
    });
    setFollowToBeRemoved(selectedFollow);
  };

  const resetFollowsToBeRemoved = () => {
    setFollowToBeRemoved(null);
  };

  const handleUnfollowConfirmation = async () => {
    captureEvent({
      type: 'signal: unfollow',
      payload: {
        followId: followToBeRemoved?.id,
      },
      sendToAmplitude: true,
    });

    setRemovingFollow(true);

    if (!followToBeRemoved?.id) {
      return;
    }

    try {
      await dispatch(unfollow(followToBeRemoved.id)).unwrap();

      setFollowToBeRemoved(null);
    } catch (e) {
      setError(true);
    } finally {
      setRemovingFollow(false);
    }
  };

  return {
    follows,
    removingFollow,
    followToBeRemoved,
    removeFollowError,
    handleUnfollow,
    handleFollowMember,
    handleUnfollowConfirmation,
    resetFollowsToBeRemoved,
  };
};

export default useSignalFollows;
